import React from 'react'
import Icon, { type IconName } from '../icons/Icon'

interface PlaidLinkPanelProps {
  hasLinkedInstitutions: boolean
  isLinking: boolean
  isLoading: boolean
  plaidReady: boolean
  linkToken: string | null
  error: string | null
  onLinkAccount: () => void
}

/**
 * Presentation-only panel for the "link an institution" call to action. Plaid
 * initialization, OAuth handling, and orchestration stay in AccountsPage; this
 * component only renders the section and surfaces the link button click.
 */
const BENEFITS: { icon: IconName; text: string }[] = [
  { icon: 'target', text: 'Use real balances and transaction data instead of estimates' },
  { icon: 'layers', text: 'Link multiple institutions, not just one bank' },
  { icon: 'gauge', text: 'See aggregate totals and institution-level reconnect status' },
  { icon: 'lock', text: 'Vault Financial uses Plaid for secure, read-only account access' },
]

const PlaidLinkPanel: React.FC<PlaidLinkPanelProps> = ({
  hasLinkedInstitutions,
  isLinking,
  isLoading,
  plaidReady,
  linkToken,
  error,
  onLinkAccount,
}) => {
  return (
    <div className="link-account-section">
      <div className="plaid-info-card">
        <h2>Why Link Your Accounts?</h2>
        <ul className="plaid-benefits">
          {BENEFITS.map(({ icon, text }) => (
            <li key={text}>
              <Icon name={icon} className="plaid-benefit-icon" />
              <span>{text}</span>
            </li>
          ))}
        </ul>
        <p className="plaid-consent-copy">
          By continuing, you authorize Vault Financial and Plaid to access the selected account data
          needed for your analysis. Review our <a href="/privacy/">Privacy Policy</a>,{' '}
          <a href="/terms/">Terms of Service</a>, and <a href="/support/">Support</a> details.
        </p>
      </div>

      <button
        onClick={onLinkAccount}
        disabled={isLinking || isLoading}
        className="link-account-btn"
      >
        {isLinking ? (
          <>
            <span className="spinner"></span>
            Opening Plaid...
          </>
        ) : hasLinkedInstitutions ? (
          '+ Link Another Institution'
        ) : (
          '+ Link a Financial Institution'
        )}
      </button>

      {!plaidReady && linkToken && !error && (
        <p className="loading-hint">Preparing secure connection...</p>
      )}
    </div>
  )
}

export default PlaidLinkPanel
